import { sanitizeUmamiPayload } from '~/utils/analyticsPrivacy'

type RuntimeBoolean = boolean | string | null | undefined

function runtimeBoolean(value: RuntimeBoolean, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }

  return fallback
}

function runtimeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

export default defineNuxtPlugin(() => {
  if (['/q/respond', '/audit-room'].includes(window.location.pathname)) return
  const runtimeConfig = useRuntimeConfig()
  const umamiConfig = runtimeConfig.public.analytics.umami

  const enabled = runtimeBoolean(umamiConfig.enabled, false)

  if (!enabled) return

  const scriptUrl = runtimeString(umamiConfig.scriptUrl)
  const websiteId = runtimeString(umamiConfig.websiteId)
  const hostUrl = runtimeString(umamiConfig.hostUrl)
  const domains = runtimeString(umamiConfig.domains)

  if (!scriptUrl || !websiteId) {
    console.warn(
      '[Morning Analytics] Umami est activé mais NUXT_PUBLIC_ANALYTICS_UMAMI_SCRIPT_URL ou NUXT_PUBLIC_ANALYTICS_UMAMI_WEBSITE_ID est absent.',
    )

    return
  }

  const autoPageview = runtimeBoolean(umamiConfig.autoPageview, false)
  const performance = runtimeBoolean(umamiConfig.performance, true)
  const excludeSearch = runtimeBoolean(umamiConfig.excludeSearch, true)
  const excludeHash = runtimeBoolean(umamiConfig.excludeHash, true)
  const doNotTrack = runtimeBoolean(umamiConfig.doNotTrack, true)

  const recorderEnabled = runtimeBoolean(umamiConfig.recorderEnabled, false)
  const recorderScriptUrl = runtimeString(umamiConfig.recorderScriptUrl)

  /*
   * Dernière barrière de confidentialité.
   *
   * Cette fonction est appelée par Umami immédiatement avant chaque envoi.
   * Même un futur appel direct à window.umami sera donc normalisé.
   */
  window.morningUmamiBeforeSend = (_type, payload) =>
    sanitizeUmamiPayload(payload, window.location.origin)

  if (recorderEnabled && !recorderScriptUrl) {
    console.warn(
      '[Morning Analytics] Le recorder Umami est activé mais NUXT_PUBLIC_ANALYTICS_UMAMI_RECORDER_SCRIPT_URL est absent.',
    )
  }

  useHead({
    script: [
      {
        key: 'morning-umami-analytics',
        src: scriptUrl,
        defer: true,

        'data-morning-analytics': 'umami',
        'data-website-id': websiteId,

        'data-auto-pageview': String(autoPageview),
        'data-performance': String(performance),
        'data-exclude-search': String(excludeSearch),
        'data-exclude-hash': String(excludeHash),
        'data-do-not-track': String(doNotTrack),

        'data-before-send': 'morningUmamiBeforeSend',

        ...(hostUrl ? { 'data-host-url': hostUrl } : {}),
        ...(domains ? { 'data-domains': domains } : {}),
      },

      ...(recorderEnabled && recorderScriptUrl
        ? [
            {
              key: 'morning-umami-recorder',
              src: recorderScriptUrl,
              defer: true,
              'data-morning-analytics': 'umami-recorder',
              'data-website-id': websiteId,
            },
          ]
        : []),
    ],
  })
})
